import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const toKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export function AvailabilityCalendar({ vehicleId, value, onSelect }) {
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [days, setDays] = useState({});
  const monthKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;

  useEffect(() => {
    if (!vehicleId) return;
    api
      .get("/availability", { params: { month: monthKey, vehicle_id: vehicleId } })
      .then((r) => setDays(r.data.days || {}))
      .catch(() => {});
  }, [vehicleId, monthKey]);

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Monday-first
    const count = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = Array(offset).fill(null);
    for (let d = 1; d <= count; d++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    return arr;
  }, [cursor]);

  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const atThisMonth = cursor.getFullYear() === today.getFullYear() && cursor.getMonth() === today.getMonth();

  return (
    <div className="border border-[#2E3340] bg-[#1E2128] p-4 sm:p-5" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => shift(-1)}
          disabled={atThisMonth}
          data-testid="cal-prev-month"
          className="p-1.5 border border-[#2E3340] text-zinc-400 hover:border-amber-500/50 hover:text-amber-300 transition-colors disabled:opacity-30"
          aria-label="Previous month"
        >
          <ChevronLeft size={14} />
        </button>
        <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.3em] text-amber-400" data-testid="cal-month-label">
          {cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </p>
        <button
          type="button"
          onClick={() => shift(1)}
          data-testid="cal-next-month"
          className="p-1.5 border border-[#2E3340] text-zinc-400 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
          aria-label="Next month"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <p key={w} className="text-center font-mono-lux text-[9px] uppercase tracking-[0.15em] text-zinc-600 py-1">
            {w}
          </p>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <span key={`empty-${i}`} />;
          const key = toKey(d);
          const status = days[key];
          const past = d < today;
          const unavailable = past || status === "blackout" || status === "booked";
          const selected = value === key;
          let cls = "text-zinc-300 border-[#2E3340] hover:border-amber-500/60 hover:text-amber-200";
          if (selected) cls = "bg-amber-500 text-black border-amber-500 font-bold";
          else if (past) cls = "text-zinc-700 border-transparent cursor-not-allowed";
          else if (status === "blackout" || status === "booked") cls = "text-red-400/60 line-through border-transparent cursor-not-allowed";
          const title = past
            ? "Past"
            : status === "blackout"
              ? "Unavailable"
              : status === "booked"
                ? "Fully committed"
                : status === "limited"
                  ? "Nearly full — a few slots remain"
                  : "Available";
          return (
            <button
              key={key}
              type="button"
              disabled={unavailable}
              onClick={() => onSelect(key)}
              title={title}
              data-testid={`cal-day-${key}`}
              className={`relative h-9 text-xs font-medium border transition-colors duration-200 ${cls}`}
            >
              {d.getDate()}
              {status === "limited" && !selected && (
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-amber-500" />
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        <span className="flex items-center gap-1.5 font-mono-lux text-[9px] uppercase tracking-[0.15em] text-zinc-500">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> Nearly full
        </span>
        <span className="flex items-center gap-1.5 font-mono-lux text-[9px] uppercase tracking-[0.15em] text-zinc-500">
          <span className="text-red-400/70 line-through">15</span> Unavailable
        </span>
        <span className="flex items-center gap-1.5 font-mono-lux text-[9px] uppercase tracking-[0.15em] text-zinc-500">
          Live availability for your vehicle
        </span>
      </div>
    </div>
  );
}
